import React, { useEffect, useState, useMemo, useCallback } from 'react'
import moment from 'moment'
import { Formik, Form } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  fetchCurrentUser,
  updateUserProfile,
  clearUpdateStatus,
} from 'store/currentUserManager'
import { fetchAndHandleArchetypes } from 'store/css-admin/userManager/operations'
import { domains } from 'lib/config'
import { jobRolesLabel, jobRoles, schoolLookupLabel } from 'lib/meta'
import { userProfileSchema } from 'lib/schemas'
import AccountDetailsFirstTimeUserAndPromptToCompleteHeader from 'components/AccountDetailsFirstTimeUserAndPromptToCompleteHeader/AccountDetailsFirstTimeUserAndPromptToCompleteHeader'
import AccountDetailsFormikController from 'components/AccountDetailsFormikController'
import ErrorBoundary from 'components/ErrorBoundary'
import SchoolLookUp from 'components/SchoolLookUp'
import RegisteredTrademarkIcon from 'components/RegisteredTrademarkIcon'
import {
  Archetype,
  CurrentUser,
  RootState,
  SchoolLookUpSiteData,
  UpdateUserProfileRequest,
} from 'types'
import { AppDispatch } from 'store/types'
import {
  Container,
  ErrorContainer,
  Header,
  Required,
  SchoolLookUpContainer,
  StyledHr,
  SubmitButton,
  SubmitSection,
} from './EditProfiles.styles'

const PROFILE_TTL_MIN = 5

// Extended type to handle form-specific fields
interface ExtendedUser extends Omit<CurrentUser, 'archetypes'> {
  archetypes?: (Archetype | number)[]
  promptToComplete?: boolean
}

const EditProfileForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { pathname } = useLocation()

  const {
    archetypes,
    currentUser,
    currentUserFetchedAt,
    isUpdating,
    updateSuccess,
  } = useSelector((state: RootState) => ({
    archetypes:
      (state.userManager?.archetypes?.data as Archetype[] | null) || null,
    currentUser: state.currentUserManager.currentUser as
      | ExtendedUser
      | undefined,
    currentUserFetchedAt: state.currentUserManager.currentUserFetchedAt,
    isUpdating: state.currentUserManager.isUpdating,
    updateSuccess: state.currentUserManager.updateSuccess,
  }))

  const [siteData, setSiteData] = useState<SchoolLookUpSiteData>({
    city: '',
    country: '',
    region: '',
    netsuiteCustomerId: null,
    organizationName: '',
    companyName: '',
    nsCustomerId: '',
  })

  const isFirstTimeUser = pathname === '/account-details/first-time'
  const promptToComplete = pathname === '/account-details/prompt-to-complete'
  const isSSO = currentUser?.isSSO || false
  const buttonText = isFirstTimeUser || promptToComplete ? 'Finish' : 'Save'

  // Initialize siteData from currentUser (map API fields to SchoolLookUp fields)
  useEffect(() => {
    if (!currentUser) return

    const shouldClearData = currentUser.hasInactiveNetSuiteCustomer
    const netsuiteCustomerId = shouldClearData
      ? null
      : currentUser.netSuiteCustomerId ?? null
    const isSchoolNotListed = netsuiteCustomerId === 0

    setSiteData({
      city: shouldClearData ? '' : currentUser.city || '',
      country: shouldClearData ? '' : currentUser.country || '',
      region: shouldClearData ? '' : currentUser.region || '',
      netsuiteCustomerId,
      // When school is not listed (netsuiteCustomerId === 0), siteName goes to organizationName
      organizationName: isSchoolNotListed ? currentUser.siteName || '' : '',
      companyName: isSchoolNotListed
        ? ''
        : shouldClearData
        ? ''
        : currentUser.siteName || '',
      nsCustomerId: shouldClearData
        ? ''
        : String(currentUser.netSuiteCustomerId || ''),
    })
  }, [currentUser])

  // Handle successful profile update
  useEffect(() => {
    if (updateSuccess) {
      dispatch(clearUpdateStatus())
      if (isFirstTimeUser || promptToComplete) {
        window.location.href = domains.CURRICULUM_APP_DOMAIN
      }
    }
  }, [dispatch, updateSuccess, isFirstTimeUser, promptToComplete])

  useEffect(() => {
    const shouldRefetch =
      currentUserFetchedAt &&
      moment() > moment(currentUserFetchedAt).add(PROFILE_TTL_MIN, 'm')

    if (!currentUserFetchedAt || shouldRefetch) {
      dispatch(fetchCurrentUser())
    }
    dispatch(fetchAndHandleArchetypes())
  }, [dispatch, currentUserFetchedAt])

  const handleSchoolSelection = useCallback((data: SchoolLookUpSiteData) => {
    setSiteData(data)
  }, [])

  const initialValues = useMemo((): ExtendedUser => {
    if (!currentUser) return {} as ExtendedUser

    const doesJobRoleExist = jobRoles.some(
      role => role.id === currentUser.title,
    )
    const shouldResetTitle =
      (currentUser.title && !doesJobRoleExist) || promptToComplete

    return {
      ...currentUser,
      title: shouldResetTitle ? '' : currentUser.title || '',
      archetypes: promptToComplete
        ? []
        : (currentUser.archetypes || []).map(a =>
            typeof a === 'object' ? a.id : a,
          ),
    }
  }, [currentUser, promptToComplete])

  const handleSubmit = useCallback(
    async (
      values: ExtendedUser,
      actions: { setSubmitting: (isSubmitting: boolean) => void },
    ) => {
      const siteName =
        siteData.netsuiteCustomerId === 0 || !siteData.netsuiteCustomerId
          ? siteData.organizationName
          : siteData.companyName

      const reqData: UpdateUserProfileRequest = {
        archetypes: (values.archetypes || []).map(a =>
          typeof a === 'object' ? a.id : a,
        ),
        city: siteData.city,
        contactPhoneNumber: values.contactPhoneNumber || null,
        country: siteData.country,
        email: values.email,
        firstName: values.firstName || '',
        id: values.id,
        lastName: values.lastName || '',
        mobilePhoneNumber: values.mobilePhoneNumber || null,
        netsuiteCustomerId: siteData.netsuiteCustomerId ?? 0,
        region: siteData.region || null,
        siteName: siteName || '',
        title: values.title || '',
      }

      await dispatch(updateUserProfile(reqData))
      actions.setSubmitting(false)
    },
    [dispatch, siteData],
  )

  if (!currentUser) return null

  // Determine if SchoolLookUp should be shown
  const hasCompleteData =
    currentUser.city &&
    currentUser.country &&
    currentUser.siteName &&
    currentUser.netSuiteCustomerId !== 0

  const showSchoolLookup =
    !promptToComplete || !hasCompleteData || !siteData.country || !siteData.city

  const isFallback =
    promptToComplete && hasCompleteData && (!siteData.country || !siteData.city)

  return (
    <ErrorBoundary>
      <Container>
        {isFirstTimeUser || promptToComplete ? (
          <AccountDetailsFirstTimeUserAndPromptToCompleteHeader
            isFirstTimeUser={isFirstTimeUser}
            promptToComplete={promptToComplete}
          />
        ) : (
          <Header>Account Details</Header>
        )}
        <StyledHr />
        <Required>* Required</Required>
        <Formik<ExtendedUser>
          enableReinitialize
          initialValues={{ ...initialValues, promptToComplete }}
          onSubmit={handleSubmit}
          validationSchema={userProfileSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ values, errors, setFieldValue }) => {
            const formErrors: Record<string, any> = {
              ...errors,
            }
            if ('country' in formErrors) delete formErrors.country
            if ('city' in formErrors) delete formErrors.city
            if ('companyName' in formErrors) delete formErrors.companyName
            const hasNoFormErrors = Object.keys(formErrors).length === 0

            const hasTitle = !!values.title
            const hasArchetypes =
              values.archetypes && values.archetypes.length > 0
            const hasValidName =
              values.isSSO || (values.firstName && values.lastName)
            const hasValidLocation =
              promptToComplete || (siteData.country && siteData.city)
            const hasValidOrganization =
              promptToComplete ||
              siteData.netsuiteCustomerId === 0 ||
              siteData.organizationName ||
              siteData.companyName?.trim()

            const isValid =
              hasNoFormErrors &&
              hasTitle &&
              hasArchetypes &&
              hasValidName &&
              hasValidLocation &&
              hasValidOrganization

            return (
              <Form {...({} as any)}>
                {!(isFirstTimeUser || promptToComplete) && (
                  <AccountDetailsFormikController
                    control="userDetails"
                    ErrorContainer={ErrorContainer}
                    errors={errors}
                    isSSO={isSSO}
                    names={['email', 'firstName', 'lastName']}
                    values={[values.email, values.firstName, values.lastName]}
                  />
                )}
                <AccountDetailsFormikController
                  control="radio"
                  ErrorContainer={ErrorContainer}
                  errors={errors.title}
                  jobRoles={jobRoles}
                  label={jobRolesLabel}
                  name="title"
                  value={values.title}
                />
                <AccountDetailsFormikController
                  archetypesData={archetypes}
                  control="checkbox"
                  ErrorContainer={ErrorContainer}
                  errors={errors.archetypes}
                  label={
                    <>
                      How are you involved with Second Step
                      <RegisteredTrademarkIcon /> programs? Select all that
                      apply:*
                    </>
                  }
                  name="archetypes"
                  setFieldValue={setFieldValue}
                  values={values.archetypes}
                />
                {showSchoolLookup && (
                  <SchoolLookUpContainer>
                    <label>
                      {schoolLookupLabel}
                      {isFallback && ' (Update your information)'}
                    </label>
                    <SchoolLookUp
                      fireOnLoad={false}
                      onSelection={handleSchoolSelection}
                      siteData={siteData}
                    />
                  </SchoolLookUpContainer>
                )}
                <SubmitSection
                  isFirstTimeUser={isFirstTimeUser || promptToComplete}
                >
                  <SubmitButton
                    disabled={!isValid || isUpdating}
                    type="button"
                    onClick={async e => {
                      e.preventDefault()
                      if (isValid && !isUpdating) {
                        await handleSubmit(values, { setSubmitting: () => {} })
                      }
                    }}
                  >
                    {buttonText}
                  </SubmitButton>
                </SubmitSection>
              </Form>
            )
          }}
        </Formik>
      </Container>
    </ErrorBoundary>
  )
}

export default EditProfileForm
